import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, Space, Typography, notification, Select } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import PageWrapper from '../../../ui/PageWrapper';
import { InternshipTypeDtoResponse } from '../../../interfaces/InternshipDto';
import { InternshipService } from '../../../services/InternshipService';
import { DictionaryCatalogDtoResponse } from '../../../interfaces/DictionaryCatalogDtoResponse';

const { Option } = Select;

const InternshipTypes = () => {
    const [data, setData] = useState<InternshipTypeDtoResponse[]>([]);
    const [categories, setCategories] = useState<DictionaryCatalogDtoResponse[]>([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingItem, setEditingItem] = useState<InternshipTypeDtoResponse | null>(null);
    const [form] = Form.useForm();

    useEffect(() => {
        fetchInternshipTypes();
        fetchCategories();
    }, []);

    const fetchInternshipTypes = async () => {
        setLoading(true);
        try {
            const response = await InternshipService.getTypes();
            setData(response.data);
        } catch (error) {
            notification.error({ message: "Error fetching data" });
        } finally {
            setLoading(false);
        }
    };
    const fetchCategories = async () => {
        try {
            const response = await InternshipService.getCategories();
            setCategories(response.data);
        } catch (error) {
            notification.error({ message: "Error fetching categories" });
        }
    };

    const handleAdd = () => {
        setEditingItem(null);
        form.resetFields();
        setIsModalVisible(true);
    };

    const handleEdit = (record: InternshipTypeDtoResponse) => {
        setEditingItem(record);
        form.setFieldsValue({
            ...record,
            categoryId: record.category ? record.category.id : undefined
        });
        setIsModalVisible(true);
    };

    const handleDelete = (id: number) => {
        Modal.confirm({
            title: 'Are you sure you want to delete this item?',
            onOk: async () => {
                setLoading(true);
                try {
                    await InternshipService.deleteType(id);
                    notification.success({ message: "Internship type deleted" });
                    fetchInternshipTypes();
                } catch (error) {
                    notification.error({ message: "Error deleting internship type" });
                } finally {
                    setLoading(false);
                }
            },
        });
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            if (editingItem) {
                await InternshipService.updateType(editingItem.id, values);
                notification.success({ message: "Internship type successfully updated" });
            } else {
                await InternshipService.createType(values);
                notification.success({ message: "Internship type successfully created" });
            }

            setIsModalVisible(false);
            fetchInternshipTypes();
        } catch (error) {
            notification.error({ message: "Error saving internship type" });
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        { title: 'Name (KZ)', dataIndex: 'name', key: 'name' },
        { title: 'Name (RU)', dataIndex: 'nameRu', key: 'nameRu' },
        { title: 'Name (EN)', dataIndex: 'nameEn', key: 'nameEn' },
        {
            title: 'Category (KZ/RU/EN)',
            key: 'category',
            render: (record: InternshipTypeDtoResponse) =>
                record.category
                    ? `${record.category.name} / ${record.category.nameRu} / ${record.category.nameEn}`
                    : 'No Category'
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (record: InternshipTypeDtoResponse) => (
                <Space>
                    <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} />
                    <Button icon={<DeleteOutlined />} danger onClick={() => handleDelete(record.id)} />
                </Space>
            ),
        },
    ];

    return (
        <PageWrapper>
            <Typography.Title level={1}>Internship Types</Typography.Title>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd} style={{ marginBottom: 16 }}>
                Add Internship Type
            </Button>
            <Table columns={columns} dataSource={data} loading={loading} rowKey="id" />
            <Modal
                title={editingItem ? 'Edit Internship Type' : 'Add Internship Type'}
                visible={isModalVisible}
                onOk={handleSave}
                onCancel={() => setIsModalVisible(false)}
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="name" label="Name (KZ)" >
                        <Input />
                    </Form.Item>
                    <Form.Item name="nameRu" label="Name (RU)" >
                        <Input />
                    </Form.Item>
                    <Form.Item name="nameEn" label="Name (EN)" >
                        <Input />
                    </Form.Item>
                    <Form.Item name="categoryId" label="Category" rules={[{ required: true, message: 'Please select a category' }]}>
                        <Select placeholder="Select a category">
                            {categories.map(category => (
                                <Option key={category.id} value={category.id}>
                                    {category.nameRu}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
        </PageWrapper>
    );
};

export default InternshipTypes;

